// Color-mode provider: owns light/dark state, persists the user's choice to
// localStorage (default light), builds the MUI theme, applies CssBaseline, and
// mirrors colors into the legacy :root CSS variables so SCSS follows the theme.
//
// Usage:
//   <AppThemeProvider><App/></AppThemeProvider>
//   const { mode, toggleColorMode } = useColorMode();

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import { createAppTheme, applyCssVarBridge } from "./index";

const STORAGE_KEY = "ui-color-mode";

const ColorModeContext = createContext({
  mode: "light",
  toggleColorMode: () => {},
  setMode: () => {},
});

export const useColorMode = () => useContext(ColorModeContext);

const readInitialMode = () => {
  if (typeof window === "undefined") return "light";
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch (e) {
    /* localStorage may be unavailable (private mode) — fall through */
  }
  return "light";
};

export const AppThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(readInitialMode);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  // Persist choice + keep legacy CSS variables in sync with the active theme.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) {
      /* ignore persistence failures */
    }
    applyCssVarBridge(theme);
  }, [mode, theme]);

  const contextValue = useMemo(
    () => ({
      mode,
      setMode: (next) => setMode(next === "dark" ? "dark" : "light"),
      toggleColorMode: () => setMode((prev) => (prev === "dark" ? "light" : "dark")),
    }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default AppThemeProvider;
